/**
 * Rutinas (Req. 10): lista con la última vez, detalle, edición, duplicar,
 * borrar y crear las precargadas de Janeth o de Fernando al confirmarlas.
 *
 * Borrar una rutina no toca los entrenamientos registrados: su routineId pasa
 * a null y conservan su nombre copiado (design.md, 7.9; Req. 10.4).
 */

import { db, leerSettings } from './schema';
import { nuevoId } from './ids';
import { enEscritura } from './tx';
import type { Exercise, Modalidad, PersonaRutinas, Routine, RoutineExercise } from './types';
import {
  descansoConfirmado,
  PERSONAS,
  PLANTILLAS_DE_RUTINAS,
  type PlantillaDelGimnasio,
  type RutinaDePlantilla,
} from '@/datos/rutinas-precargadas';
import { ahoraIso, type FechaLocal } from '@/domain/dates';
import { normalizar } from '@/domain/busqueda';
import { ErrorValidacion } from '@/domain/errors';
import { limpiarPausaYVariante, modalidadDe } from '@/domain/modalidad';

export interface RutinaEnLista {
  rutina: Routine;
  ejercicios: number;
  /** Los ejercicios en su orden, para reconocer una precargada creada antes de la 0.3.3. */
  exerciseIds: string[];
  /** Fecha del último entrenamiento terminado con esta rutina (Req. 10.5). */
  ultimaVez: FechaLocal | null;
}

/** Lo que se necesita de una rutina del teléfono para saber si ya es una precargada. */
export type RutinaParaPlantilla = Pick<Routine, 'nombre' | 'plantilla' | 'modalidad'> & { exerciseIds?: readonly string[] };

export interface EjercicioDeRutina {
  item: RoutineExercise;
  ejercicio: Exercise | undefined;
}

export interface RutinaDetalle {
  rutina: Routine;
  ejercicios: EjercicioDeRutina[];
  ultimaVez: FechaLocal | null;
}

const porOrden = <T extends { orden: number }>(a: T, b: T) => a.orden - b.orden;

async function ultimasVeces(): Promise<Map<string, FechaLocal>> {
  const terminados = await db.workouts.where('estado').equals('terminado').toArray();
  const ultimas = new Map<string, FechaLocal>();
  for (const w of terminados) {
    if (w.routineId === null) continue;
    const previa = ultimas.get(w.routineId);
    if (previa === undefined || w.fecha > previa) ultimas.set(w.routineId, w.fecha);
  }
  return ultimas;
}

/** LECTURA PURA. */
export async function leerRutinasEnLista(): Promise<RutinaEnLista[]> {
  const [rutinas, items, ultimas] = await Promise.all([
    db.routines.orderBy('orden').toArray(),
    db.routineExercises.toArray(),
    ultimasVeces(),
  ]);
  return rutinas.map((rutina) => {
    const suyos = items.filter((i) => i.routineId === rutina.id).sort(porOrden);
    return {
      rutina,
      ejercicios: suyos.length,
      exerciseIds: suyos.map((i) => i.exerciseId),
      ultimaVez: ultimas.get(rutina.id) ?? null,
    };
  });
}

/** LECTURA PURA. null si no existe. */
export async function leerRutina(id: string): Promise<RutinaDetalle | null> {
  const rutina = await db.routines.get(id);
  if (rutina === undefined) return null;
  const items = (await db.routineExercises.where('routineId').equals(id).toArray()).sort(porOrden);
  const ejercicios = await db.exercises.bulkGet(items.map((i) => i.exerciseId));
  const ultimas = await ultimasVeces();
  return {
    rutina,
    ejercicios: items.map((item, i) => ({ item, ejercicio: ejercicios[i] })),
    ultimaVez: ultimas.get(id) ?? null,
  };
}

const firmaDeEjercicios = (ids: readonly string[]) => ids.join('|');

/** El nombre sin el " (copia)" que agrega Duplicar, para comparar. */
const nombreBase = (nombre: string) => normalizar(nombre.replace(/(\s*\(copia\))+$/i, ''));

/**
 * ¿La rutina del teléfono salió de esta rutina precargada? Sí, si guarda su
 * clave (aunque se le haya cambiado el nombre). Si no trae clave (creada
 * antes de la 0.3.3, o una copia hecha con Duplicar), solo en la misma
 * modalidad: si se llama igual, sin contar acentos, mayúsculas ni "(copia)",
 * o si tiene los mismos ejercicios en el mismo orden. Así una rutina de casa
 * nunca pasa por una del gimnasio, ni al revés.
 */
export function esCopiaDe(r: RutinaParaPlantilla, p: RutinaDePlantilla): boolean {
  if (typeof r.plantilla === 'string') return r.plantilla === p.id;
  if (modalidadDe(r) !== modalidadDe(p)) return false;
  if (nombreBase(r.nombre) === normalizar(p.nombre)) return true;
  const ids = r.exerciseIds ?? [];
  return ids.length > 0 && firmaDeEjercicios(ids) === firmaDeEjercicios(p.ejercicios.map((e) => e.exerciseId));
}

/**
 * Las rutinas de la plantilla que todavía no están en el teléfono: las que
 * ninguna rutina del teléfono es copia suya (esCopiaDe).
 */
export function rutinasQueFaltan(
  rutinas: readonly RutinaParaPlantilla[],
  plantilla: readonly RutinaDePlantilla[],
): RutinaDePlantilla[] {
  return plantilla.filter((p) => !rutinas.some((r) => esCopiaDe(r, p)));
}

/** La persona de la rutina precargada del gimnasio de la que salió esta rutina, si salió de una. */
export function plantillaDeRutina(r: RutinaParaPlantilla): PlantillaDelGimnasio | null {
  for (const persona of PERSONAS) {
    const plantilla = PLANTILLAS_DE_RUTINAS[persona];
    if (plantilla.rutinas.some((p) => esCopiaDe(r, p))) return plantilla;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Descansos confirmados (26 oct 2026)
// ---------------------------------------------------------------------------

export interface CambioDeDescanso {
  itemId: string;
  exerciseId: string;
  /** descansoSeg: solo o por separado; descansoBiserieSeg: al terminar la biserie. */
  campo: 'descansoSeg' | 'descansoBiserieSeg';
  antes: number | null;
  despues: number;
}

export interface RutinaConCambios {
  routineId: string;
  nombre: string;
  persona: PersonaRutinas;
  cambios: CambioDeDescanso[];
}

/**
 * Los descansos que cambiarían en las rutinas del gimnasio que salieron de
 * una precargada (y sus copias): el confirmado de su persona en cada
 * ejercicio, también en los que se agregaron después, y en las biseries el
 * mayor de sus ejercicios al terminar todos. Las rutinas propias, las de casa
 * y los entrenamientos no se tocan. Pura.
 */
export function cambiosDeDescanso(rutinas: readonly Routine[], items: readonly RoutineExercise[]): RutinaConCambios[] {
  const resultado: RutinaConCambios[] = [];
  for (const rutina of [...rutinas].sort(porOrden)) {
    if (modalidadDe(rutina) !== 'gimnasio') continue;
    const suyos = items.filter((i) => i.routineId === rutina.id).sort(porOrden);
    const plantilla = plantillaDeRutina({ ...rutina, exerciseIds: suyos.map((i) => i.exerciseId) });
    if (plantilla === null) continue;
    const cambios: CambioDeDescanso[] = [];
    for (const item of suyos) {
      const confirmado = descansoConfirmado(plantilla, item.exerciseId);
      if (item.descansoSeg !== confirmado) {
        cambios.push({ itemId: item.id, exerciseId: item.exerciseId, campo: 'descansoSeg', antes: item.descansoSeg, despues: confirmado });
      }
      const clave = item.biserie ?? null;
      if (clave === null) continue;
      const deBiserie = suyos.filter((x) => (x.biserie ?? null) === clave).map((x) => descansoConfirmado(plantilla, x.exerciseId));
      const alTerminar = Math.max(...deBiserie);
      if ((item.descansoBiserieSeg ?? null) !== alTerminar) {
        cambios.push({
          itemId: item.id,
          exerciseId: item.exerciseId,
          campo: 'descansoBiserieSeg',
          antes: item.descansoBiserieSeg ?? null,
          despues: alTerminar,
        });
      }
    }
    if (cambios.length > 0) resultado.push({ routineId: rutina.id, nombre: rutina.nombre, persona: plantilla.persona, cambios });
  }
  return resultado;
}

/** LECTURA PURA. */
export async function leerCambiosDeDescanso(): Promise<RutinaConCambios[]> {
  const [rutinas, items] = await Promise.all([db.routines.toArray(), db.routineExercises.toArray()]);
  return cambiosDeDescanso(rutinas, items);
}

/**
 * Pone los descansos confirmados en las rutinas del gimnasio que salieron de
 * una precargada, en una sola transacción. Solo cambia esos descansos: no
 * toca series, repeticiones, notas, cargas, entrenamientos ni otras rutinas.
 * Un entrenamiento abierto conserva los suyos. ESCRIBE. Devuelve cuántos
 * descansos cambió.
 */
export async function aplicarDescansosConfirmados(ahora: string = ahoraIso()): Promise<number> {
  return enEscritura([db.routines, db.routineExercises], async () => {
    const [rutinas, items] = await Promise.all([db.routines.toArray(), db.routineExercises.toArray()]);
    let cambiados = 0;
    for (const r of cambiosDeDescanso(rutinas, items)) {
      for (const c of r.cambios) {
        await db.routineExercises.update(c.itemId, { [c.campo]: c.despues });
        cambiados++;
      }
      await db.routines.update(r.routineId, { actualizadoEn: ahora });
    }
    return cambiados;
  });
}

export function faltanDePlantilla(rutinas: readonly RutinaParaPlantilla[], plantilla: readonly RutinaDePlantilla[]): boolean {
  return rutinasQueFaltan(rutinas, plantilla).length > 0;
}

/** Las notas del ejercicio en la rutina: el nombre del archivo, si es otro, y las indicaciones. */
function notasDePlantilla(e: RutinaDePlantilla['ejercicios'][number]): string | null {
  const partes = [e.nombreEnArchivo === undefined ? null : `En tu tabla: ${e.nombreEnArchivo}.`, e.notas];
  const texto = partes.filter((p): p is string => p !== null && p !== '').join(' ');
  return texto === '' ? null : texto;
}

/**
 * Crea las rutinas que falten de una plantilla ya revisada por el usuario, en
 * una sola transacción y en la modalidad de cada una. Solo agrega: no cambia
 * ni borra rutinas, ejercicios ni entrenamientos que ya estén. Un descanso
 * sin confirmar toma el predeterminado del teléfono. ESCRIBE. Devuelve los
 * ids creados.
 */
export async function crearRutinasDePlantilla(
  plantilla: readonly RutinaDePlantilla[],
  ahora: string = ahoraIso(),
): Promise<string[]> {
  const { descansoPredeterminadoSeg } = await leerSettings();
  return enEscritura([db.routines, db.routineExercises, db.exercises], async () => {
    const existentes = await db.routines.toArray();
    const items = (await db.routineExercises.toArray()).sort(porOrden);
    const faltan = rutinasQueFaltan(
      existentes.map((r) => ({ ...r, exerciseIds: items.filter((i) => i.routineId === r.id).map((i) => i.exerciseId) })),
      plantilla,
    );
    for (const p of faltan) {
      for (const e of p.ejercicios) {
        if ((await db.exercises.get(e.exerciseId)) === undefined) {
          throw new ErrorValidacion(`Falta el ejercicio ${e.exerciseId} en el catálogo. No se creó ninguna rutina.`);
        }
      }
    }
    let orden = existentes.reduce((max, r) => Math.max(max, r.orden), -1) + 1;
    const creadas: string[] = [];
    for (const p of faltan) {
      const id = nuevoId('rut');
      await db.routines.add({
        id,
        nombre: p.nombre,
        notas: p.notas,
        orden: orden++,
        dias: [...p.dias],
        plantilla: p.id,
        modalidad: p.modalidad ?? 'gimnasio',
        creadoEn: ahora,
        actualizadoEn: ahora,
      });
      await db.routineExercises.bulkAdd(
        p.ejercicios.map((e, i) => ({
          id: nuevoId('rje'),
          routineId: id,
          exerciseId: e.exerciseId,
          orden: i,
          series: e.series,
          repsMin: e.repsMin,
          repsMax: e.repsMax,
          descansoSeg: e.descansoSeg ?? descansoPredeterminadoSeg,
          notas: notasDePlantilla(e),
          biserie: e.biserie,
          descansoBiserieSeg: e.descansoBiserieSeg,
          ...(e.pausaSeg === undefined && e.variante === undefined ? {} : limpiarPausaYVariante(e)),
          ...(e.porLado === true ? { porLado: true } : {}),
        })),
      );
      creadas.push(id);
    }
    return creadas;
  });
}

/** Rutina nueva y vacía, en la modalidad que se está viendo en Gym. ESCRIBE. */
export async function crearRutina(
  nombre: string,
  id: string = nuevoId('rut'),
  modalidad: Modalidad = 'gimnasio',
): Promise<string> {
  const limpio = nombre.trim().replace(/\s+/g, ' ');
  if (limpio === '') throw new ErrorValidacion('Escribe el nombre de la rutina.', 'nombre');
  if (limpio.length > 60) throw new ErrorValidacion('El nombre es demasiado largo.', 'nombre');
  const ahora = ahoraIso();
  await enEscritura([db.routines], async () => {
    if ((await db.routines.get(id)) !== undefined) return; // doble toque
    const orden = (await db.routines.toArray()).reduce((max, r) => Math.max(max, r.orden), -1) + 1;
    await db.routines.add({
      id,
      nombre: limpio,
      notas: null,
      orden,
      dias: [],
      modalidad: modalidadDe({ modalidad }),
      creadoEn: ahora,
      actualizadoEn: ahora,
    });
  });
  return id;
}

/**
 * Nombre, notas, días y, si se indica, la modalidad. Cambiar la modalidad
 * solo mueve la rutina de lista: sus entrenamientos conservan la suya.
 * ESCRIBE.
 */
export async function guardarDatosDeRutina(
  id: string,
  datos: Pick<Routine, 'nombre' | 'notas'> & { dias: number[]; modalidad?: Modalidad },
): Promise<void> {
  const nombre = datos.nombre.trim().replace(/\s+/g, ' ');
  if (nombre === '') throw new ErrorValidacion('Escribe el nombre de la rutina.', 'nombre');
  if (nombre.length > 60) throw new ErrorValidacion('El nombre es demasiado largo.', 'nombre');
  const dias = [...new Set(datos.dias)].filter((d) => Number.isInteger(d) && d >= 0 && d <= 6).sort();
  await enEscritura([db.routines], async () => {
    await db.routines.update(id, {
      nombre,
      notas: datos.notas === null || datos.notas.trim() === '' ? null : datos.notas.trim(),
      dias,
      ...(datos.modalidad === undefined ? {} : { modalidad: modalidadDe({ modalidad: datos.modalidad }) }),
      actualizadoEn: ahoraIso(),
    });
  });
}

export function validarEjercicioDeRutina(re: RoutineExercise): void {
  if (!Number.isInteger(re.series) || re.series < 1 || re.series > 20) {
    throw new ErrorValidacion('Las series deben ser de 1 a 20.', 'series');
  }
  for (const v of [re.repsMin, re.repsMax]) {
    if (v !== null && (!Number.isInteger(v) || v < 1 || v > 100)) {
      throw new ErrorValidacion('Las repeticiones deben ser de 1 a 100.', 'reps');
    }
  }
  if (re.repsMin !== null && re.repsMax !== null && re.repsMin > re.repsMax) {
    throw new ErrorValidacion('El mínimo de repeticiones no puede ser mayor que el máximo.', 'reps');
  }
  for (const v of [re.descansoSeg, re.descansoBiserieSeg ?? null]) {
    if (v !== null && (!Number.isInteger(v) || v < 0 || v > 900)) {
      throw new ErrorValidacion('El descanso debe ser de 0 a 15 minutos.', 'descanso');
    }
  }
}

/** Objetivo de un ejercicio de la rutina. ESCRIBE. */
export async function guardarEjercicioDeRutina(re: RoutineExercise): Promise<void> {
  validarEjercicioDeRutina(re);
  const casa = re.pausaSeg === undefined && re.variante === undefined ? {} : limpiarPausaYVariante(re);
  await enEscritura([db.routineExercises, db.routines], async () => {
    await db.routineExercises.put({ ...re, ...casa, notas: re.notas === null || re.notas.trim() === '' ? null : re.notas.trim() });
    await db.routines.update(re.routineId, { actualizadoEn: ahoraIso() });
  });
}

/**
 * Al final de la rutina. Descanso: en una rutina del gimnasio que salió de
 * una precargada, el confirmado de esa persona; en las demás, el
 * predeterminado del teléfono. ESCRIBE.
 */
export async function agregarEjercicioARutina(routineId: string, exerciseId: string): Promise<string> {
  const ajustes = await leerSettings();
  const ejercicio = await db.exercises.get(exerciseId);
  if (ejercicio === undefined) throw new ErrorValidacion('Ese ejercicio no existe.');
  const sinReps = ejercicio.tipo === 'tiempo' || ejercicio.tipo === 'distancia';
  const id = nuevoId('rje');
  await enEscritura([db.routineExercises, db.routines], async () => {
    const rutina = await db.routines.get(routineId);
    const items = (await db.routineExercises.where('routineId').equals(routineId).toArray()).sort(porOrden);
    const plantilla =
      rutina === undefined || modalidadDe(rutina) !== 'gimnasio'
        ? null
        : plantillaDeRutina({ ...rutina, exerciseIds: items.map((i) => i.exerciseId) });
    await db.routineExercises.add({
      id,
      routineId,
      exerciseId,
      orden: items.reduce((max, i) => Math.max(max, i.orden), -1) + 1,
      series: 3,
      repsMin: sinReps ? null : 8,
      repsMax: sinReps ? null : 12,
      descansoSeg: plantilla === null ? ajustes.descansoPredeterminadoSeg : descansoConfirmado(plantilla, exerciseId),
      notas: null,
      biserie: null,
      descansoBiserieSeg: null,
    });
    await db.routines.update(routineId, { actualizadoEn: ahoraIso() });
  });
  return id;
}

/** Quita el ejercicio; si era de una biserie de dos, el otro queda solo. ESCRIBE. */
export async function quitarEjercicioDeRutina(id: string): Promise<void> {
  await enEscritura([db.routineExercises, db.routines], async () => {
    const item = await db.routineExercises.get(id);
    if (item === undefined) return;
    await db.routineExercises.delete(id);
    if ((item.biserie ?? null) !== null) {
      const resto = (await db.routineExercises.where('routineId').equals(item.routineId).toArray()).filter(
        (i) => i.biserie === item.biserie,
      );
      if (resto.length === 1 && resto[0] !== undefined) {
        await db.routineExercises.update(resto[0].id, { biserie: null, descansoBiserieSeg: null });
      }
    }
    await db.routines.update(item.routineId, { actualizadoEn: ahoraIso() });
  });
}

/** Sube o baja un lugar (Req. 10.3). ESCRIBE. */
export async function moverEjercicioDeRutina(id: string, direccion: -1 | 1): Promise<void> {
  await enEscritura([db.routineExercises], async () => {
    const item = await db.routineExercises.get(id);
    if (item === undefined) return;
    const items = (await db.routineExercises.where('routineId').equals(item.routineId).toArray()).sort(porOrden);
    const i = items.findIndex((x) => x.id === id);
    const otro = items[i + direccion];
    if (otro === undefined) return;
    await db.routineExercises.update(item.id, { orden: otro.orden });
    await db.routineExercises.update(otro.id, { orden: item.orden });
  });
}

/**
 * Junta el ejercicio con el siguiente en una biserie, o la deshace. El
 * descanso de la biserie empieza con el mayor de los dos. ESCRIBE.
 */
export async function alternarBiserieConSiguiente(id: string, descansoBiserieSeg: number): Promise<void> {
  await enEscritura([db.routineExercises], async () => {
    const item = await db.routineExercises.get(id);
    if (item === undefined) return;
    const items = (await db.routineExercises.where('routineId').equals(item.routineId).toArray()).sort(porOrden);
    const i = items.findIndex((x) => x.id === id);
    const siguiente = items[i + 1];
    if (siguiente === undefined) return;
    if ((item.biserie ?? null) !== null && item.biserie === siguiente.biserie) {
      // Deshacer: los dos quedan solos (y los demás de esa biserie también)
      for (const x of items.filter((y) => y.biserie === item.biserie)) {
        await db.routineExercises.update(x.id, { biserie: null, descansoBiserieSeg: null });
      }
      return;
    }
    const usadas = new Set(items.map((x) => x.biserie).filter((b): b is string => typeof b === 'string'));
    const clave = (item.biserie ?? null) ?? siguiente.biserie ?? ['A', 'B', 'C', 'D', 'E', 'F'].find((c) => !usadas.has(c)) ?? `B${i}`;
    for (const x of [item, siguiente]) {
      await db.routineExercises.update(x.id, { biserie: clave, descansoBiserieSeg });
    }
  });
}

/** Copia con "(copia)". ESCRIBE. */
export async function duplicarRutina(id: string): Promise<string> {
  const nueva = nuevoId('rut');
  await enEscritura([db.routines, db.routineExercises], async () => {
    const rutina = await db.routines.get(id);
    if (rutina === undefined) throw new ErrorValidacion('Esa rutina ya no existe.');
    const items = await db.routineExercises.where('routineId').equals(id).toArray();
    const orden = (await db.routines.toArray()).reduce((max, r) => Math.max(max, r.orden), -1) + 1;
    const ahora = ahoraIso();
    // La copia es propia: no ocupa el lugar de la rutina precargada
    await db.routines.add({
      ...rutina,
      id: nueva,
      nombre: `${rutina.nombre} (copia)`.slice(0, 60),
      orden,
      plantilla: null,
      creadoEn: ahora,
      actualizadoEn: ahora,
    });
    await db.routineExercises.bulkAdd(items.map((x) => ({ ...x, id: nuevoId('rje'), routineId: nueva })));
  });
  return nueva;
}

/** Borra la rutina; sus entrenamientos se conservan sin rutina (Req. 10.4). ESCRIBE. */
export async function borrarRutina(id: string): Promise<void> {
  await enEscritura([db.routines, db.routineExercises, db.workouts], async () => {
    await db.routineExercises.where('routineId').equals(id).delete();
    await db.workouts.where('routineId').equals(id).modify({ routineId: null });
    await db.routines.delete(id);
  });
}
